// BranchSettings.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Building2,
  MapPin,
  Phone,
  Mail,
  Save
} from 'lucide-react';
import toast from 'react-hot-toast';

import { apiHelper } from '@services/api';
import Button, { IconButton } from '@components/common/Button';
import Modal, { ModalBody, ModalFooter } from '@components/common/Modal';
import Loading from '@components/common/Loading';
import { Badge } from '@components/common/StatusBadge';

const BranchSettings = () => {
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const queryClient = useQueryClient();

  // Fetch branches
  const { data: branches, isLoading } = useQuery(
    ['branches-settings'],
    async () => {
      const response = await apiHelper.get('/branches');
      return response.data;
    }
  );

  // Branch mutations
  const branchMutation = useMutation(
    async ({ action, data, id }) => {
      switch (action) {
        case 'create':
          return await apiHelper.post('/branches', data);
        case 'update':
          return await apiHelper.put(`/branches/${id}`, data);
        case 'delete':
          return await apiHelper.delete(`/branches/${id}`);
        default:
          throw new Error('Invalid action');
      }
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['branches-settings']);
        toast.success(`Branch ${variables.action}d successfully`);
        setShowBranchModal(false);
        setEditingBranch(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  );

  const handleCreateBranch = () => {
    setEditingBranch(null);
    setShowBranchModal(true);
  };

  const handleEditBranch = (branch) => {
    setEditingBranch(branch);
    setShowBranchModal(true);
  };

  const handleDeleteBranch = (branch) => {
    if (window.confirm(`Are you sure you want to delete ${branch.name}?`)) {
      branchMutation.mutate({ action: 'delete', id: branch.id });
    }
  };

  const styles = {
    page: { display: 'block', gap: 16 },
    headerCard: {
      background: '#fff',
      borderRadius: 14,
      padding: 16,
      border: '1px solid #eef2f6',
      boxShadow: '0 10px 30px rgba(2,6,23,0.04)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
    logoBox: {
      width: 40,
      height: 40,
      borderRadius: 12,
      background: 'var(--primary-100, rgba(59,130,246,0.08))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: 16
    },
    card: {
      background: '#fff',
      borderRadius: 14,
      padding: 16,
      border: '1px solid #eef2f6',
      boxShadow: '0 10px 30px rgba(2,6,23,0.04)',
      display: 'flex',
      flexDirection: 'column',
      gap: 12
    },
    metaRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
    smallText: { fontSize: 13, color: 'var(--gray-600, #6b7280)' },
    addressText: { fontSize: 14, color: 'var(--gray-700, #374151)', lineHeight: 1.4 },
    footerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 12, borderTop: '1px solid #f3f6f9' },
    emptyCard: { textAlign: 'center', padding: 24 }
  };

  if (isLoading) {
    return (
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #eef2f6', padding: 24 }}>
        <Loading size="lg" text="Loading branches..." />
      </div>
    );
  }

  return (
    <>
      <motion.div
        style={styles.page}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32 }}
      >
        {/* Header */}
        <div style={styles.headerCard}>
          <div style={styles.headerLeft}>
            <div style={styles.logoBox}>
              <Building2 style={{ width: 20, height: 20, color: 'var(--primary-600)' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--gray-900)' }}>
                                Branch Management
              </h2>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--gray-600)' }}>
                                Manage your organization's branch locations
              </p>
            </div>
          </div>

          <Button variant="primary" leftIcon={<Plus style={{ width: 16, height: 16 }} />} onClick={handleCreateBranch}>
                        Add Branch
          </Button>
        </div>

        {/* Branches Grid */}
        <div style={styles.grid}>
          {branches && branches.length > 0 ? (
            branches.map((branch, index) => (
              <motion.div
                key={branch.id}
                style={styles.card}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, delay: index * 0.06 }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--primary-100, rgba(59,130,246,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Building2 style={{ width: 20, height: 20, color: 'var(--primary-600)' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--gray-900)' }}>{branch.name}</div>
                      <div style={{ ...styles.smallText, marginTop: 4 }}>{branch.code}</div>
                    </div>
                  </div>

                  <div style={{ alignSelf: 'center' }}>
                    <Badge variant={branch.is_active ? 'success' : 'default'} size="sm">
                      {branch.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {branch.address && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <MapPin style={{ width: 18, height: 18, color: 'var(--gray-400)', marginTop: 2 }} />
                      <div style={styles.addressText}>
                        <div>{branch.address}</div>
                        <div>{branch.city}{branch.state ? `, ${branch.state}` : ''}</div>
                        {branch.pincode && <div>{branch.pincode}</div>}
                      </div>
                    </div>
                  )}

                  {branch.phone && (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Phone style={{ width: 16, height: 16, color: 'var(--gray-400)' }} />
                      <div style={styles.smallText}>{branch.phone}</div>
                    </div>
                  )}

                  {branch.email && (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Mail style={{ width: 16, height: 16, color: 'var(--gray-400)' }} />
                      <div style={styles.smallText}>{branch.email}</div>
                    </div>
                  )}
                </div>

                <div style={styles.footerRow}>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                                        Created {new Date(branch.created_at).toLocaleDateString()}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <IconButton variant="ghost" size="sm" onClick={() => handleEditBranch(branch)} tooltip="Edit branch">
                      <Edit style={{ width: 16, height: 16 }} />
                    </IconButton>

                    <IconButton variant="ghost" size="sm" onClick={() => handleDeleteBranch(branch)} tooltip="Delete branch" style={{ color: 'var(--error-600)' }}>
                      <Trash2 style={{ width: 16, height: 16 }} />
                    </IconButton>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div style={{ ...styles.card, gridColumn: '1 / -1', textAlign: 'center' }}>
              <div style={{ fontSize: 36, color: 'var(--gray-300)', marginBottom: 8 }}>
                <Building2 style={{ width: 48, height: 48, color: 'var(--gray-300)' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--gray-900)' }}>No branches configured</h3>
              <p style={{ marginTop: 8, color: 'var(--gray-600)' }}>Create your first branch to get started.</p>
              <div style={{ marginTop: 12 }}>
                <Button variant="primary" leftIcon={<Plus style={{ width: 14, height: 14 }} />} onClick={handleCreateBranch}>
                                    Create Branch
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Branch Modal */}
      <BranchModal
        isOpen={showBranchModal}
        onClose={() => {
          setShowBranchModal(false);
          setEditingBranch(null);
        }}
        branch={editingBranch}
        onSave={(data) => {
          const action = editingBranch ? 'update' : 'create';
          const id = editingBranch?.id;
          branchMutation.mutate({ action, data, id });
        }}
        loading={branchMutation.isLoading}
      />
    </>
  );
};

// Branch Modal Component (inline-style)
const BranchModal = ({ isOpen, onClose, branch, onSave, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    phone: '',
    email: '',
    is_active: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (branch) {
      setFormData({
        name: branch.name || '',
        code: branch.code || '',
        address: branch.address || '',
        city: branch.city || '',
        state: branch.state || '',
        country: branch.country || 'India',
        pincode: branch.pincode || '',
        phone: branch.phone || '',
        email: branch.email || '',
        is_active: branch.is_active ?? true
      });
    } else {
      setFormData({
        name: '',
        code: '',
        address: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',
        phone: '',
        email: '',
        is_active: true
      });
    }
    setErrors({});
  }, [branch, isOpen]);

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Branch name is required';
    if (!formData.code.trim()) newErrors.code = 'Branch code is required';
    else if (formData.code.length > 10) newErrors.code = 'Branch code must be 10 characters or less';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Pincode must be 6 digits';
    if (formData.phone && !/^\+?[1-9]\d{1,14}$/.test(formData.phone)) newErrors.phone = 'Invalid phone number format';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Inline styles for form layout
  const formStyles = {
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 },
    sectionTitle: { fontSize: 16, fontWeight: 600, margin: '8px 0 12px 0', color: 'var(--gray-900)' },
    label: { display: 'block', fontSize: 13, color: 'var(--gray-700)', marginBottom: 6 },
    input: (hasError) => ({
      width: '100%',
      padding: '10px 12px',
      borderRadius: 10,
      border: hasError ? '1px solid rgba(239,68,68,0.2)' : '1px solid #e6eef3',
      outline: 'none',
      fontSize: 14,
      boxSizing: 'border-box'
    }),
    textarea: (hasError) => ({
      width: '100%',
      padding: '10px 12px',
      borderRadius: 10,
      border: hasError ? '1px solid rgba(239,68,68,0.2)' : '1px solid #e6eef3',
      outline: 'none',
      fontSize: 14,
      minHeight: 56,
      resize: 'vertical',
      boxSizing: 'border-box'
    }),
    errorText: { marginTop: 6, fontSize: 13, color: 'var(--error-600, #ef4444)' },
    checkboxRow: { display: 'flex', alignItems: 'center', gap: 8 }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={branch ? 'Edit Branch' : 'Create Branch'} size="lg">
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div style={formStyles.grid}>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={formStyles.sectionTitle}>Basic Information</div>
            </div>

            <div>
              <label style={formStyles.label}>Branch Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                style={formStyles.input(!!errors.name)}
                placeholder="Mumbai Central Hub"
              />
              {errors.name && <div style={formStyles.errorText}>{errors.name}</div>}
            </div>

            <div>
              <label style={formStyles.label}>Branch Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                style={formStyles.input(!!errors.code)}
                placeholder="MUM"
                maxLength={10}
              />
              {errors.code && <div style={formStyles.errorText}>{errors.code}</div>}
            </div>

            <div style={{ gridColumn: '1 / -1', marginTop: 6 }}>
              <div style={formStyles.sectionTitle}>Address Information</div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={formStyles.label}>Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                style={formStyles.textarea(!!errors.address)}
                placeholder="Plot No. 45, Andheri East Industrial Area"
              />
            </div>

            <div>
              <label style={formStyles.label}>City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                style={formStyles.input(!!errors.city)}
                placeholder="Mumbai"
              />
              {errors.city && <div style={formStyles.errorText}>{errors.city}</div>}
            </div>

            <div>
              <label style={formStyles.label}>State *</label>
              <select
                value={formData.state}
                onChange={(e) => handleChange('state', e.target.value)}
                style={formStyles.input(!!errors.state)}
              >
                <option value="">Select State</option>
                {indianStates.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {errors.state && <div style={formStyles.errorText}>{errors.state}</div>}
            </div>

            <div>
              <label style={formStyles.label}>Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                style={formStyles.input(false)}
              />
            </div>

            <div>
              <label style={formStyles.label}>Pincode</label>
              <input
                type="text"
                value={formData.pincode}
                onChange={(e) => handleChange('pincode', e.target.value)}
                style={formStyles.input(!!errors.pincode)}
                placeholder="400069"
                maxLength={6}
              />
              {errors.pincode && <div style={formStyles.errorText}>{errors.pincode}</div>}
            </div>

            <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
              <div style={formStyles.sectionTitle}>Contact Information</div>
            </div>

            <div>
              <label style={formStyles.label}>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                style={formStyles.input(!!errors.phone)}
                placeholder="+91-22-28202800"
              />
              {errors.phone && <div style={formStyles.errorText}>{errors.phone}</div>}
            </div>

            <div>
              <label style={formStyles.label}>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                style={formStyles.input(!!errors.email)}
                placeholder="mumbai@rdxlogistics.com"
              />
              {errors.email && <div style={formStyles.errorText}>{errors.email}</div>}
            </div>

            <div style={{ gridColumn: '1 / -1', marginTop: 6 }}>
              <label style={formStyles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
                <span style={{ fontSize: 14, color: 'var(--gray-700)' }}>Active</span>
              </label>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
                        Cancel
          </Button>

          <Button type="submit" loading={loading} leftIcon={<Save style={{ width: 14, height: 14 }} />}>
            {branch ? 'Update Branch' : 'Create Branch'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default BranchSettings;
